/**
 * Settings, GitHub (admins): create Agora's GitHub App from a manifest in one
 * click, install it on the repos to read, change the repo list later.
 */
import { useEffect, useState } from "react";
import { ErrorNote, Fold } from "../components/ui";
import { api, ApiError } from "../lib/api";

interface Status {
  workersReady?: boolean;
  settingsUrl?: string | null;
  available: boolean;
  app: { slug: string; owner: string; htmlUrl: string; installed: boolean; installUrl: string } | null;
}

const NOTES: Record<string, { ok: boolean; text: string }> = {
  connected: { ok: true, text: "GitHub is connected. Projects can now use the repos you picked." },
  failed: { ok: false, text: "GitHub could not be connected. Try again; the page on GitHub must be finished within half an hour." },
};

export function GithubSettings() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note] = useState(() => NOTES[new URLSearchParams(location.search).get("github") ?? ""] ?? null);
  useEffect(() => {
    api<Status>("/api/github").then(setStatus, (err) => setError(err instanceof ApiError ? err.message : "Could not load the GitHub status."));
    if (location.search) history.replaceState(null, "", `${location.pathname}${location.hash}`);
  }, []);

  // The manifest goes to GitHub as a real form post, so GitHub shows its own Create page.
  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ action: string; manifest: string }>("/api/github/manifest", {});
      const form = document.createElement("form");
      form.method = "post";
      form.action = r.action;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "manifest";
      input.value = r.manifest;
      form.append(input);
      document.body.append(form);
      form.submit();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start.");
      setBusy(false);
    }
  }

  return (
    <Fold
      id="settings-github"
      anchor="github"
      title="GitHub"
      side={status?.app?.installed && <span className="chip ok">connected</span>}
      summary={status?.app ? `App ${status.app.slug} on ${status.app.owner}${status.workersReady ? ", coding workers ready" : ""}.` : "Not connected."}
    >
      <p className="muted-text">
        Agora reads the repos of its projects through its own GitHub App: docs (CLAUDE.md, plan.md), files, commits, pull requests and CI. Read only for now. You choose which repos it can see.
      </p>
      {note && <p className={`notice-line ${note.ok ? "ok" : ""}`}>{note.text}</p>}
      <ErrorNote error={error} />
      {!status && !error && <p className="muted-text">Loading</p>}
      {status && !status.available && <p className="muted-text">The server is not set up to store keys yet.</p>}
      {status?.available && !status.app && (
        <div className="actions">
          <button className="btn primary small" type="button" disabled={busy} onClick={() => void connect()}>
            {busy ? "Opening GitHub" : "Connect GitHub"}
          </button>
          <small className="muted-text">GitHub opens: click Create GitHub App, then pick the repos and click Install.</small>
        </div>
      )}
      {status?.app && (
        <ul className="rows">
          <li>
            <div>
              <b>{status.app.slug}</b>
              <small>
                App of {status.app.owner}. {status.app.installed ? "Installed: projects can use the repos you picked." : "Created but not installed yet."}
              </small>
            </div>
            <a className={`btn small ${status.app.installed ? "ghost" : "primary"}`} href={status.app.installUrl}>
              {status.app.installed ? "Change repos" : "Install"}
            </a>
          </li>
          {status.app.installed && (
            <li>
              <div>
                <b>Coding workers</b>
                <small>
                  {status.workersReady
                    ? "Ready: the app may start workers, open, comment on and merge pull requests."
                    : "Need write permissions: Actions, Contents, Pull requests and Issues. Open the app's permissions, set them to Read and write, save, then accept them on the installation."}
                </small>
              </div>
              {!status.workersReady && status.settingsUrl && (
                <a className="btn small primary" href={status.settingsUrl} target="_blank" rel="noreferrer">
                  Permissions
                </a>
              )}
            </li>
          )}
        </ul>
      )}
    </Fold>
  );
}
